import React, { useRef } from 'react';
import {
  Box,
  Card,
  Stack,
  Typography,
  IconButton,
  useTheme,
  Divider,
  Button,
  CardContent,
  Tooltip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import { CallRounded, SmsRounded, ContentCopyRounded } from '@mui/icons-material';

interface BusinessAboutSectionOriginalProps {
  businessName: string;
  contactName?: string;
  phoneNumber?: string;
}

export const BusinessAboutSectionOriginal: React.FC<BusinessAboutSectionOriginalProps> = ({
  businessName,
  contactName,
  phoneNumber
}) => {
  const theme = useTheme();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [copied, setCopied] = React.useState(false);

  const scrollBy = (dx: number) => scrollerRef.current?.scrollBy({ left: dx, behavior: 'smooth' });
  const phoneProvided = Boolean(phoneNumber?.trim());

  const copyPhone = async () => {
    try {
      await navigator.clipboard.writeText(phoneNumber || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  
  return (
    <Box id="about" sx={{ scrollMarginTop: 80 }}>
      <Box maxWidth="1100px" mx="auto" px={{ xs: 2, sm: 3 }} py={{ xs: 3, sm: 4 }}>
        <Card
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            background:
              theme.palette.mode === 'dark'
                ? 'rgba(22,22,22,0.85)'
                : 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(6px)'
          }}
        >
          {/* Header — larger logo + matching title weight; no ring/underline/gradient */}
          <Stack direction="row" alignItems="center" spacing={{ xs: 2, sm: 3 }} sx={{ mb: 1 }}>

            {/* Business Name */}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 34, sm: 40, md: 48 }, // big but not overflowing
                  fontWeight: 900,
                  lineHeight: 1.1,
                  color: 'text.primary',
                  letterSpacing: -0.5,
                  wordBreak: 'break-word' // wrap long names
                }}
              >
                {businessName}
              </Typography>
              {/* subtle accent bar to anchor the header (uses primary color) */}
              <Box sx={{ mt: 1, height: 3, width: 72, borderRadius: 2, bgcolor: 'primary.main' }} />
            </Box>
          </Stack>

          {/* Contact Info */}
          <Stack spacing={1.25} sx={{ mb: 2, mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="body1">
                <strong>Owner:</strong> {contactName || 'Not provided'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body1">
                <strong>Phone:</strong>{' '}
                {phoneProvided ? (
                  <Box
                    component="a"
                    href={`tel:${phoneNumber}`}
                    sx={{ 
                      color: 'primary.main', 
                      textDecoration: 'none', 
                      fontWeight: 600,
                      '&:hover': {
                        color: 'accent.main',
                      }
                    }}
                  >
                    {phoneNumber}
                  </Box>
                ) : (
                  'Not provided'
                )}
              </Typography>
            </Stack>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={1}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<CallRounded />}
              href={phoneProvided ? `tel:${phoneNumber}` : undefined}
              disabled={!phoneProvided}
            >
              Call
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              startIcon={<SmsRounded />}
              href={phoneProvided ? `sms:${phoneNumber}` : undefined}
              disabled={!phoneProvided}
            >
              Text
            </Button>
            <Tooltip title={copied ? 'Copied!' : 'Copy'}>
              <span>
                <IconButton
                  color="default"
                  onClick={copyPhone}
                  disabled={!phoneProvided}
                  sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
                  aria-label="Copy phone number"
                >
                  <ContentCopyRounded fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Card>
      </Box>
    </Box>
  );
};
